/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAuth } from "../../../../context/AuthContext";
import * as drive from "../../../../api/drive";
import { formatDate } from "../format";

const TEXT_BUTTON_SX = { color: "text.color", textTransform: "none" };
const ACCENT_BUTTON_SX = { backgroundColor: "accent.solid", color: "#fff", textTransform: "none" };

/** Flat per-file discussion thread — no editing, no threading. */
const CommentsDialog = ({ open, file, onClose }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!file) return;
    try {
      setComments(await drive.listComments(file.id));
    } catch (err) {
      setError(err.message || "Could not load comments.");
      setComments([]);
    }
  }, [file]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBody("");
    setComments(null);
    load();
  }, [open, load]);

  const add = async () => {
    if (!body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await drive.addComment(file.id, body.trim());
      setBody("");
      await load();
    } catch (err) {
      setError(err.message || "Could not post that comment.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (commentId) => {
    setBusy(true);
    try {
      await drive.deleteComment(file.id, commentId);
      await load();
    } catch (err) {
      setError(err.message || "Could not delete that comment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Comments — {file?.name}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {comments === null ? (
          <Typography sx={{ fontSize: 13.5 }}>Loading…</Typography>
        ) : comments.length === 0 ? (
          <Typography sx={{ fontSize: 13.5, color: "secondaryText.color", mb: 2 }}>
            No comments yet.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
            {comments.map((c) => (
              <Box key={c.id} sx={{ display: "flex", gap: 1 }}>
                <Avatar sx={{ width: 26, height: 26, fontSize: 12 }}>
                  {(c.user_full_name || c.user_email || "?").charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                      {c.user_full_name || c.user_email || "Someone"}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "secondaryText.color" }}>
                      {formatDate(c.created_at)}
                    </Typography>
                    {c.user_id === user?.sub && (
                      <Tooltip title="Delete comment">
                        <IconButton size="small" disabled={busy} onClick={() => remove(c.id)} sx={{ ml: "auto" }}>
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{c.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={4}
            placeholder="Add a comment"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={busy}
          />
          <Button variant="contained" sx={ACCENT_BUTTON_SX} disabled={busy || !body.trim()} onClick={add}>
            Post
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={TEXT_BUTTON_SX}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommentsDialog;
